(function (global) {
  'use strict';

  const OFFER_RULES = Object.freeze({
    box: Object.freeze({key: 'box', size: 1, label: 'Build your box', openEnded: true})
  });

  const QUANTITY_TIERS = Object.freeze([
    Object.freeze({min: 1, max: 4, percent: 0}),
    Object.freeze({min: 5, max: 8, percent: 5}),
    Object.freeze({min: 9, max: 11, percent: 7}),
    Object.freeze({min: 12, max: Infinity, percent: 10})
  ]);

  const normalizePlans = plans => (Array.isArray(plans) ? plans : [])
    .filter(plan => plan && plan.id)
    .map(plan => ({
      id: Number(plan.id),
      name: String(plan.name || 'Subscribe'),
      price: Math.max(0, Number(plan.price || 0))
    }));

  const discountedUnitPrice = (price, percent) => {
    const cents = Math.max(0, Number(price || 0));
    const discountCents = Math.floor(cents * Math.max(0, Number(percent || 0)) / 100);
    return cents - discountCents;
  };

  const normalizeCatalog = catalog => (Array.isArray(catalog) ? catalog : [])
    .filter(item => item && item.variantId && item.available !== false)
    .map(item => ({
      variantId: Number(item.variantId),
      productId: Number(item.productId || 0),
      flavorKey: String(item.flavorKey || item.variantId),
      flavorName: String(item.flavorName || item.title || 'Flavor'),
      title: String(item.title || item.flavorName || 'Steak Slices'),
      price: Math.max(0, Number(item.price || 0)),
      image: String(item.image || ''),
      url: String(item.url || ''),
      sellingPlans: normalizePlans(item.sellingPlans)
    }));

  function normalizeTiers(values) {
    if (!Array.isArray(values)) throw Error('Quantity tiers are required');
    const sorted = values.map(t=>({quantity:Number(t.quantity),percent:Number(t.percent)})).sort((a,b)=>a.quantity-b.quantity);
    if (sorted.some((t,i)=>!Number.isSafeInteger(t.quantity)||t.quantity<1||t.quantity>100||!Number.isFinite(t.percent)||t.percent<=0||t.percent>50||i>0&&(t.quantity===sorted[i-1].quantity||t.percent<=sorted[i-1].percent))) throw Error('Invalid quantity tiers');
    return [{min:0,max:(sorted[0]?.quantity??Infinity)-1,percent:0},...sorted.map((t,i)=>({min:t.quantity,max:(sorted[i+1]?.quantity??Infinity)-1,percent:t.percent}))];
  }

  function getQuantityTier(count, tiers = QUANTITY_TIERS) {
    const normalized = Math.max(0, Math.floor(Number(count) || 0));
    const tier = tiers.find(candidate => normalized >= candidate.min && normalized <= candidate.max) || tiers[0];
    const tierIndex = tiers.indexOf(tier);
    const nextTier = tiers[tierIndex + 1] || null;
    return Object.freeze({
      ...tier,
      nextTier,
      remainingToNext: nextTier ? Math.max(0, nextTier.min - normalized) : 0
    });
  }

  function createBundleState(catalog, configuredTiers = null) {
    let tiers = configuredTiers === null ? QUANTITY_TIERS : normalizeTiers(configuredTiers);
    const items = normalizeCatalog(catalog);
    if (!items.length) throw new Error('A bundle catalog is required');
    let quantities = Object.fromEntries(items.map(item => [item.variantId, 0]));

    const selectedCount = () => Object.values(quantities).reduce((sum, quantity) => sum + quantity, 0);
    const snapshot = () => {
      const count = selectedCount();
      const lines = items
        .filter(item => quantities[item.variantId] > 0)
        .map(item => ({...item, quantity: quantities[item.variantId], linePrice: item.price * quantities[item.variantId]}));
      const subtotalPrice = lines.reduce((sum, line) => sum + line.linePrice, 0);
      const tier = getQuantityTier(count, tiers);
      const discountedPrice = lines.reduce(
        (sum, line) => sum + discountedUnitPrice(line.price, tier.percent) * line.quantity,
        0
      );
      const discountAmount = subtotalPrice - discountedPrice;
      return Object.freeze({
        offer: OFFER_RULES.box,
        quantities: Object.freeze({...quantities}),
        selectedCount: count,
        remainingCount: count ? 0 : 1,
        complete: count > 0,
        subtotalPrice,
        totalPrice: subtotalPrice,
        previewDiscountAmount: discountAmount,
        previewDiscountedPrice: discountedPrice,
        unitPrice: count ? Math.round(subtotalPrice / count) : 0,
        tier,
        tiers,
        lines: Object.freeze(lines)
      });
    };

    const setQuantity = (variantId, value) => {
      const id = Number(variantId);
      if (!(id in quantities)) return snapshot();
      const parsed = Number(value);
      if (!Number.isFinite(parsed)) return snapshot();
      quantities[id] = Math.max(0, Math.floor(parsed));
      return snapshot();
    };

    const change = (variantId, delta) => {
      const id = Number(variantId);
      if (!(id in quantities)) return snapshot();
      return setQuantity(id, quantities[id] + Number(delta || 0));
    };

    return Object.freeze({
      getSnapshot: snapshot,
      setTiers(values) { tiers = normalizeTiers(values); return snapshot(); },
      add: variantId => change(variantId, 1),
      remove: variantId => change(variantId, -1),
      setQuantity,
      setOffer(nextOffer) {
        if (nextOffer !== 'box') throw new Error(`Unknown bundle offer: ${nextOffer}`);
        return snapshot();
      },
      reset() {
        quantities = Object.fromEntries(items.map(item => [item.variantId, 0]));
        return snapshot();
      }
    });
  }

  const formatMoney = cents => {
    const amount = (Number(cents || 0) / 100).toFixed(2);
    const format = global.joburgTheme && global.joburgTheme.moneyFormat;
    return format ? format.replace(/\{\{\s*amount[^}]*\}\}/, amount) : `$${amount}`;
  };

  const parseJSON = element => {
    try { return JSON.parse(element.textContent); } catch (_) { return null; }
  };

  const commonSellingPlans = lines => {
    if (!lines.length || lines.some(line => !line.sellingPlans.length)) return [];
    const sharedIds = lines.slice(1).reduce(
      (ids, line) => ids.filter(id => line.sellingPlans.some(plan => plan.id === id)),
      lines[0].sellingPlans.map(plan => plan.id)
    );
    return lines[0].sellingPlans.filter(plan => sharedIds.includes(plan.id));
  };

  function initBundleBuilder(root) {
    const catalogNode = root.querySelector('[data-bundle-catalog]');
    const catalog = catalogNode && parseJSON(catalogNode);
    if (!catalog || !catalog.length) return;

    const policyUrl = root.dataset.quantityPolicy;
    const state = createBundleState(catalog, policyUrl ? [] : null);
    const editId = new URLSearchParams(global.location.search).get('edit_box');
    let editingBox = null, editBusy = Boolean(editId);
    const draftKey = !editId && root.classList.contains('studio-builder') ? 'joburg-studio-draft-v1' : null;
    const journalKey = 'joburg-box-edit-intent-v1';
    const journal = {get:()=>JSON.parse(global.localStorage.getItem(journalKey)||'null'),set:value=>value?global.localStorage.setItem(journalKey,JSON.stringify(value)):global.localStorage.removeItem(journalKey)};
    const cartApi = {
      read: async()=>{const r=await fetch(`${global.joburgTheme.cartUrl}.js`,{cache:'no-store'});if(!r.ok)throw Error('Cannot read your cart.');return r.json();},
      add: async items=>{const r=await fetch(`${global.joburgTheme.cartAddUrl}.js`,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({items})});if(!r.ok)throw Object.assign(Error('A pouch could not be added. Check availability and try again.'),{rejected:r.status>=400&&r.status<500});},
      update: async updates=>{const r=await fetch(`${global.joburgTheme.cartUrl}/update.js`,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({updates})});if(!r.ok)throw Error('Could not finish updating your box.');}
    };
    let draftRestored = false;
    if (draftKey && !global.location.hash.startsWith('#mix=')) {
      try {
        const draft = JSON.parse(global.localStorage.getItem(draftKey));
        if (draft && Date.now() - draft.updated < 30 * 86400000 && Array.isArray(draft.mix)) {
          draft.mix.slice(0,20).forEach(([id,quantity]) => {
            if (catalog.some(item => String(item.variantId) === String(id)) && Number.isSafeInteger(quantity) && quantity > 0 && quantity <= 1000) state.setQuantity(id,quantity);
          });
          draftRestored = state.getSnapshot().selectedCount > 0;
        }
      } catch (_) { /* Storage is optional; shopping must still work. */ }
    }
    const cards = [...root.querySelectorAll('[data-bundle-variant]')];
    const submit = root.querySelector('[data-bundle-submit]');
    const reset = root.querySelector('[data-bundle-reset]');
    const status = root.querySelector('[data-bundle-status]');
    const progress = root.querySelector('[data-bundle-progress]');
    const savings = root.querySelector('[data-bundle-savings]');
    const total = root.querySelector('[data-bundle-total]');
    const unit = root.querySelector('[data-bundle-unit]');
    const preview = root.querySelector('[data-bundle-preview-total]');
    const subscribeBox = root.querySelector('[data-bundle-subscription]');
    const subscribePending = root.querySelector('[data-bundle-subscription-pending]');
    const subscribeToggle = root.querySelector('[data-bundle-subscribe]');
    const planSelect = root.querySelector('[data-bundle-plan]');
    const subscriptionTotal = root.querySelector('[data-bundle-subscription-total]');
    const showSavingsPreview = root.dataset.savingsPreview === 'true';

    const track = (event, detail = {}) => {
      if (typeof global.joburgTrack === 'function') return global.joburgTrack(event, detail);
      if (typeof document !== 'undefined' && typeof global.CustomEvent === 'function') {
        document.dispatchEvent(new global.CustomEvent(`joburg:${event}`, {detail}));
      }
      return detail;
    };

    let builderStarted = false;
    const ensureStarted = source => {
      if (builderStarted) return;
      builderStarted = true;
      track('bundle_started', {bundle_type: 'box', source});
    };

    const renderSubscription = snapshot => {
      if (!subscribeBox || !planSelect || !subscribeToggle) return;
      const plans = commonSellingPlans(snapshot.lines.length ? snapshot.lines : catalog);
      const previousPlanId = Number(planSelect.value || 0);
      planSelect.replaceChildren(...plans.map(plan => {
        const option = document.createElement('option');
        option.value = String(plan.id);
        option.textContent = plan.name;
        return option;
      }));
      if (plans.some(plan => plan.id === previousPlanId)) planSelect.value = String(previousPlanId);
      const available = plans.length > 0;
      subscribeBox.hidden = !available;
      if (subscribePending) subscribePending.hidden = available;
      if (!available) subscribeToggle.checked = false;

      const purchaseCards = root.querySelector('[data-bundle-purchase-cards]');
      if (purchaseCards && global.JoburgPurchaseCards) {
        global.JoburgPurchaseCards.render(purchaseCards, [
          {id:0,name:'One-time purchase',detail:snapshot.complete ? `${formatMoney(snapshot.previewDiscountedPrice)} for this box · no commitment` : 'No commitment'},
          ...plans.map(plan => ({id:plan.id,name:plan.name,detail:snapshot.complete
            ? `${formatMoney(snapshot.lines.reduce((sum,line)=>sum+discountedUnitPrice(line.sellingPlans.find(p=>p.id===plan.id).price,snapshot.tier.percent)*line.quantity,0))} per delivery${snapshot.tier.percent ? ' · includes quantity savings' : ''}`
            : 'Choose your flavors to see your box price'}))
        ],subscribeToggle.checked ? Number(planSelect.value) : 0, value => {
          if(editBusy)return;
          subscribeToggle.checked=Boolean(value);
          if(value)planSelect.value=String(value);
          render(state.getSnapshot());
        });
      }

      const selectedPlanId = Number(planSelect.value || 0);
      const selectedPlan = plans.find(plan => plan.id === selectedPlanId);
      const recurringCheckoutTotal = selectedPlan
        ? snapshot.lines.reduce((sum, line) => {
            const allocation = line.sellingPlans.find(plan => plan.id === selectedPlan.id);
            return sum + discountedUnitPrice(allocation ? allocation.price : line.price, snapshot.tier.percent) * line.quantity;
          }, 0)
        : snapshot.previewDiscountedPrice;
      if (subscriptionTotal) {
        subscriptionTotal.textContent = snapshot.complete && subscribeToggle.checked && selectedPlan
          ? `Renewal: ${formatMoney(recurringCheckoutTotal)} per delivery`
          : snapshot.complete ? 'One-time purchase selected. Or choose a schedule above to save.' : 'Choose your first pouch to see your box price';
      }
      if (snapshot.complete && submit) {
        const checkoutTotal = subscribeToggle.checked && selectedPlan ? recurringCheckoutTotal : snapshot.previewDiscountedPrice;
        total.textContent = formatMoney(checkoutTotal);
        unit.textContent = `${formatMoney(Math.round(checkoutTotal / snapshot.selectedCount))} average per pouch${subscribeToggle.checked ? ' per delivery' : ''}`;
        if (preview && showSavingsPreview && subscribeToggle.checked && selectedPlan) {
          preview.textContent = `${snapshot.tier.percent > 0 ? 'Quantity + subscription savings' : 'Subscription savings'} · you save ${formatMoney(snapshot.totalPrice - checkoutTotal)} per delivery`;
        }
        submit.textContent = `Add ${snapshot.selectedCount} pouch${snapshot.selectedCount === 1 ? '' : 'es'} · ${formatMoney(checkoutTotal)}`;
      }
    };

    const render = snapshot => {
      cards.forEach(card => {
        const id = Number(card.dataset.bundleVariant);
        const quantity = snapshot.quantities[id] || 0;
        const quantityInput = card.querySelector('[data-bundle-quantity]');
        quantityInput.value = quantity;
        quantityInput.removeAttribute('max');
        card.querySelectorAll('[data-bundle-change]').forEach(button => {
          button.disabled = Number(button.dataset.bundleChange) < 0 && quantity === 0;
        });
        card.classList.toggle('is-selected', quantity > 0);
      });

      if (!snapshot.selectedCount) {
        status.textContent = 'Choose your first pouch';
        savings.textContent = 'Add any flavor. There is no minimum box size.';
      } else if (snapshot.tier.nextTier) {
        status.textContent = `${snapshot.selectedCount} pouch${snapshot.selectedCount === 1 ? '' : 'es'} in your box`;
        savings.textContent = `${snapshot.tier.remainingToNext} more to unlock ${snapshot.tier.nextTier.percent}% savings`;
      } else {
        status.textContent = `${snapshot.selectedCount} pouches in your box`;
        savings.textContent = snapshot.tier.percent ? `Top quantity tier reached · ${snapshot.tier.percent}% savings` : 'Savings are confirmed in cart and checkout.';
      }

      progress.style.width = `${Math.min(100, snapshot.selectedCount / (snapshot.tiers.at(-1).min || Infinity) * 100)}%`;
      total.textContent = formatMoney(snapshot.previewDiscountedPrice);
      unit.textContent = snapshot.selectedCount
        ? `${formatMoney(Math.round(snapshot.previewDiscountedPrice / snapshot.selectedCount))} average per pouch after quantity savings`
        : 'Choose flavors to see your total';
      submit.disabled = !snapshot.complete;
      submit.textContent = snapshot.complete ? `Add ${snapshot.selectedCount} pouch${snapshot.selectedCount === 1 ? '' : 'es'} · ${formatMoney(snapshot.previewDiscountedPrice)}` : 'Choose at least one pouch';

      if (preview) {
        preview.hidden = !showSavingsPreview || !snapshot.selectedCount;
        preview.textContent = showSavingsPreview && snapshot.selectedCount
          ? snapshot.tier.percent
            ? `${snapshot.tier.percent}% automatic quantity savings · you save ${formatMoney(snapshot.previewDiscountAmount)}`
            : snapshot.tier.nextTier ? `Quantity savings begin at ${snapshot.tier.nextTier.min} pouches.` : 'Savings are confirmed in cart and checkout.'
          : '';
      }
      renderSubscription(snapshot);
      if(editId){submit.disabled=editBusy||!editingBox||!snapshot.complete;submit.textContent=editBusy?'Loading your cart box…':'Update this box';}
      root._joburgBundleSnapshot = snapshot;
      if (draftKey) {
        try {
          if (snapshot.selectedCount) global.localStorage.setItem(draftKey,JSON.stringify({updated:Date.now(),mix:snapshot.lines.map(line=>[String(line.variantId),line.quantity])}));
          else global.localStorage.removeItem(draftKey);
        } catch (_) { /* Private browsers may disallow storage. */ }
      }
    };

    root.addEventListener('click', event => {
      if(editId&&editBusy)return;
      const changeButton = event.target.closest('[data-bundle-change]');
      if (changeButton) {
        const card = changeButton.closest('[data-bundle-variant]');
        const variantId = Number(card.dataset.bundleVariant);
        const before = state.getSnapshot();
        ensureStarted('first_item');
        const snapshot = Number(changeButton.dataset.bundleChange) > 0 ? state.add(variantId) : state.remove(variantId);
        const item = catalog.find(candidate => Number(candidate.variantId) === variantId) || {};
        if ((snapshot.quantities[variantId] || 0) !== (before.quantities[variantId] || 0)) {
          track(Number(changeButton.dataset.bundleChange) > 0 ? 'bundle_item_added' : 'bundle_item_removed', {
            bundle_type: 'box',
            variant_id: String(variantId),
            item_id: String(item.productId || ''),
            flavor: item.flavorKey || '',
            item_name: item.title || '',
            quantity: snapshot.quantities[variantId] || 0,
            price_cents: Number(item.price || 0)
          });
        }
        render(snapshot);
      }
      if (event.target.closest('[data-bundle-reset]')) render(state.reset());
    });

    const applyQuantityInput = quantityInput => {
      if(editId&&editBusy)return;
      if (!quantityInput) return;
      const card = quantityInput.closest('[data-bundle-variant]');
      if (!card) return;
      ensureStarted('typed_quantity');
      render(state.setQuantity(Number(card.dataset.bundleVariant), quantityInput.value));
    };

    root.addEventListener('input', event => applyQuantityInput(event.target.closest('[data-bundle-quantity]')));
    root.addEventListener('change', event => applyQuantityInput(event.target.closest('[data-bundle-quantity]')));
    if (subscribeToggle) subscribeToggle.addEventListener('change', () => render(state.getSnapshot()));
    if (planSelect) planSelect.addEventListener('change', () => render(state.getSnapshot()));

    submit.addEventListener('click', async () => {
      const snapshot = root._joburgBundleSnapshot;
      if (!snapshot || !snapshot.complete) return;
      if(editId){
        if(editBusy||!editingBox)return;
        editBusy=true;submit.disabled=true;submit.textContent='Updating your box…';
        const editPlanId=subscribeToggle?.checked?Number(planSelect.value||0):0;
        if(editPlanId&&!commonSellingPlans(snapshot.lines).some(p=>p.id===editPlanId)){status.textContent='Choose a schedule available for every flavor.';editBusy=false;submit.disabled=false;return;}
        try{await global.JoburgBoxEdit.save(cartApi,journal,editingBox,snapshot.lines.map(x=>({id:x.variantId,quantity:x.quantity,...(editPlanId?{selling_plan:editPlanId}:{})})),global.crypto.randomUUID());global.location.href=global.joburgTheme.cartUrl;}
        catch(error){status.textContent=error.message;editBusy=false;submit.disabled=false;submit.textContent='Finish updating your box';}
        return;
      }
      try{if(journal.get()){status.textContent='Finish your interrupted box update before adding another box. Return to your cart for the recovery link.';return;}}catch(_){/* New boxes do not require storage. */}
      const selectedPlanId = subscribeToggle && subscribeToggle.checked ? Number(planSelect.value || 0) : 0;
      const sharedPlans = commonSellingPlans(snapshot.lines);
      if (selectedPlanId && !sharedPlans.some(plan => plan.id === selectedPlanId)) {
        status.textContent = 'That subscription is not available for every selected flavor. Your one-time box is unchanged.';
        return;
      }

      submit.disabled = true;
      submit.textContent = 'Adding your box…';
      const bundleId = `joburg-box-${Date.now()}`;
      const selectedPlanTotal = selectedPlanId
        ? snapshot.lines.reduce((sum, line) => {
            const allocation = line.sellingPlans.find(plan => plan.id === selectedPlanId);
            return sum + discountedUnitPrice(allocation ? allocation.price : line.price, snapshot.tier.percent) * line.quantity;
          }, 0)
        : snapshot.previewDiscountedPrice;
      const items = snapshot.lines.map((line, index) => ({
        id: line.variantId,
        quantity: line.quantity,
        ...(selectedPlanId ? {selling_plan: selectedPlanId} : {}),
        properties: {
          _joburg_bundle_id: bundleId,
          _joburg_bundle_size: snapshot.selectedCount,
          _joburg_bundle_name: 'Build your box',
          _joburg_bundle_position: index + 1,
          _joburg_bundle_tier_percent: snapshot.tier.percent,
          _joburg_bundle_preview_savings: snapshot.previewDiscountAmount,
          _joburg_bundle_preview_total: snapshot.previewDiscountedPrice,
          _joburg_bundle_discount_status: 'Automatic Shopify quantity discount',
          _joburg_bundle_purchase_type: selectedPlanId ? 'subscription' : 'one_time',
          _joburg_subscription_plans: sharedPlans.map(plan => `${plan.id}:${plan.name}`).join('|')
        }
      }));

      try {
        const response = await fetch(`${global.joburgTheme.cartAddUrl}.js`, {
          method: 'POST',
          headers: {'Content-Type': 'application/json', Accept: 'application/json'},
          body: JSON.stringify({items})
        });
        if (!response.ok) throw new Error('Your box could not be added. Please refresh and try again.');
        if (draftKey) { try { global.localStorage.removeItem(draftKey); } catch (_) {} }
        const detail = {
          bundle_id: bundleId,
          bundle_size: snapshot.selectedCount,
          bundle_type: 'box',
          subscription: Boolean(selectedPlanId),
          selling_plan_id: selectedPlanId || undefined,
          value_cents: selectedPlanTotal,
          items: snapshot.lines.map(line => ({variant_id: line.variantId, flavor: line.flavorKey, quantity: line.quantity}))
        };
        track('bundle_completed', detail);
        track('add_to_cart', {...detail, item_name: 'Build your box', quantity: snapshot.selectedCount, source: 'bundle_builder'});
        global.location.href = global.joburgTheme.cartUrl;
      } catch (error) {
        status.textContent = error.message;
        submit.disabled = false;
        submit.textContent = `Add ${snapshot.selectedCount} pouch${snapshot.selectedCount === 1 ? '' : 'es'} · ${formatMoney(selectedPlanTotal)}`;
      }
    });

    const toolsFeedback = root.querySelector('[data-box-feedback]');
    const storageKey = 'joburg-saved-mix-v1';
    const mixValue = () => state.getSnapshot().lines.map(line => [String(line.variantId), line.quantity]);
    const restoreMix = value => {
      if (!Array.isArray(value) || !value.length || value.length > 20) throw new Error('This saved mix could not be read. Please choose your flavors again.');
      const seen = new Set();
      const valid = value.filter(entry => {
        if (!Array.isArray(entry) || entry.length !== 2) return false;
        const [id, count] = entry;
        if (!/^\d+$/.test(String(id)) || seen.has(String(id)) || !Number.isSafeInteger(count) || count < 1 || count > 1000) return false;
        seen.add(String(id));
        return catalog.some(item => String(item.variantId) === String(id));
      });
      if (!valid.length) throw new Error('None of the saved flavors are available right now. Please choose a new mix.');
      state.reset();
      valid.forEach(([id, count]) => state.setQuantity(id, count));
      if (subscribeToggle) subscribeToggle.checked = false;
      render(state.getSnapshot());
      toolsFeedback.textContent = valid.length < value.length ? 'Available flavors loaded; some saved items are unavailable or invalid. Review your mix and current prices.' : 'Mix loaded at current prices. Review quantities before adding to cart. Subscription is not selected.';
    };
    root.querySelector('[data-box-save]')?.addEventListener('click', () => {
      const mix = mixValue();
      if (!mix.length) { toolsFeedback.textContent = 'Choose at least one pouch first.'; return; }
      try { global.localStorage.setItem(storageKey, JSON.stringify(mix)); toolsFeedback.textContent = 'Saved on this device. Use Load saved mix when you return.'; }
      catch (_) { toolsFeedback.textContent = 'Your browser could not save this mix. Create a share link instead.'; }
    });
    root.querySelector('[data-box-restore]')?.addEventListener('click', () => {
      try { const saved = global.localStorage.getItem(storageKey); if (!saved) throw new Error('No mix has been saved on this device yet.'); restoreMix(JSON.parse(saved)); }
      catch (error) { toolsFeedback.textContent = error.message || 'Could not load the saved mix.'; }
    });
    root.querySelector('[data-box-share]')?.addEventListener('click', () => {
      const mix = mixValue();
      if (!mix.length) { toolsFeedback.textContent = 'Choose at least one pouch first.'; return; }
      const url = new URL('/pages/biltong?view=bundle-builder', global.location.origin);
      if (draftKey) url.searchParams.set('view','studio-builder');
      if (root.dataset.previewTheme) url.searchParams.set('preview_theme_id', root.dataset.previewTheme);
      url.hash = 'mix=' + encodeURIComponent(JSON.stringify(mix));
      const input = root.querySelector('[data-box-link]');
      input.value = url.href;
      root.querySelector('[data-box-link-label]').hidden = false;
      input.focus(); input.select();
      toolsFeedback.textContent = 'Link ready to copy. It includes flavors and quantities only—not payment or subscription details.';
    });
    render(state.getSnapshot());
    if (policyUrl) {
      let expiryTimer, requestBusy = false;
      const clearPolicy = () => render(state.setTiers([]));
      const refreshPolicy = async () => {
        if (requestBusy || !root.isConnected) return;
        requestBusy = true;
        try {
          if (policyUrl !== 'https://subscriptions.joburgmeats.com/storefront/quantity-policy') throw Error('Untrusted policy endpoint');
          const response = await fetch(policyUrl, {credentials:'omit', cache:'no-store', signal:AbortSignal.timeout(5000)});
          if (!response.ok) throw Error('Policy unavailable');
          const policy = await response.json(), now = Date.now(), end = Date.parse(policy.validUntil), checked = Date.parse(policy.checkedAt);
          if (policy.version !== 1 || policy.source !== 'verified-shopify' || !Number.isFinite(end) || !Number.isFinite(checked) || checked > now + 5000 || now - checked > 60000 || end <= now || end - checked > 60000 ||
              !Array.isArray(policy.productIds) || !catalog.every(item => policy.productIds.includes('gid://shopify/Product/' + item.productId))) throw Error('Policy scope or freshness changed');
          render(state.setTiers(policy.tiers));
          clearTimeout(expiryTimer);
          expiryTimer = setTimeout(clearPolicy, end - now);
        } catch (_) { clearTimeout(expiryTimer); clearPolicy(); }
        finally { requestBusy = false; }
      };
      refreshPolicy();
      const timer = setInterval(() => { if (!root.isConnected) {clearInterval(timer);clearTimeout(expiryTimer);} else refreshPolicy(); }, 30000);
    }
    if(editId){
      const notice=document.createElement('div');notice.className='box-edit-notice';notice.setAttribute('role','status');
      const title=document.createElement('strong');title.textContent='Editing your cart box';
      const copy=document.createElement('p');copy.textContent='Change flavors or quantities, then update. Your other boxes stay unchanged. The estimate below is for this mix alone; Shopify recalculates savings across your whole cart when you update.';
      const cancel=document.createElement('a');cancel.href=global.joburgTheme.cartUrl;cancel.textContent='Cancel and return to cart';notice.append(title,copy,cancel);root.querySelector('.page-width').prepend(notice);
      (async()=>{try{
        if(journal.get()){cancel.textContent='Return to cart — update needs completion';await global.JoburgBoxEdit.recover(cartApi,journal);global.location.href=global.joburgTheme.cartUrl;return;}
        editingBox=global.JoburgBoxEdit.open(await cartApi.read(),editId,catalog);state.reset();editingBox.mix.forEach(([id,q])=>state.setQuantity(id,q));editBusy=false;render(state.getSnapshot());
        if(editingBox.sellingPlanId&&subscribeToggle&&planSelect){subscribeToggle.checked=true;planSelect.value=String(editingBox.sellingPlanId);render(state.getSnapshot());}
      }catch(error){status.textContent=error.message;submit.disabled=true;notice.append(document.createTextNode(' '+error.message));}})();
    }
    if (draftRestored && toolsFeedback) {
      toolsFeedback.textContent = 'Welcome back—your box is restored at current prices. Review your quantities. Subscription is not selected.';
      root.querySelector('.bundle-box-tools')?.setAttribute('open','');
    }
    if (!editId && toolsFeedback && global.location.hash.startsWith('#mix=')) {
      try { restoreMix(JSON.parse(decodeURIComponent(global.location.hash.slice(5)))); }
      catch (_) { toolsFeedback.textContent = 'This shared mix could not be loaded. Please choose your flavors again.'; }
      const disclosure = root.querySelector('.bundle-box-tools');
      if (disclosure) disclosure.open = true;
    }
  }

  const api = Object.freeze({OFFER_RULES, QUANTITY_TIERS, createBundleState, getQuantityTier, normalizeTiers, normalizeCatalog, commonSellingPlans});
  global.JoburgBundleEngine = api;
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (typeof document !== 'undefined') {
    const boot = () => document.querySelectorAll('[data-joburg-bundle-builder]').forEach(initBundleBuilder);
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  }
})(typeof window !== 'undefined' ? window : globalThis);
